import { useState, useEffect } from 'react';
import { useSession } from 'next-auth/react';
import dayjs from 'dayjs';
import { listNotifications, saveNotification, Notification } from '@/api';
import AccountSectionTitle from '@/components/my-account/AccountSectionTitle';

import EyeLight from '@/assets/eye-light.svg';
import TrashLight from '@/assets/trash-light.svg';
import Tooltip from '@/components/general/Tooltip';

export default function StylusAlertsModule() {
  const { data: session } = useSession();
  const [notificationsData, setNotificationsData] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedNotifications, setSelectedNotifications] = useState<string[]>([]);
  const [expandedPanels, setExpandedPanels] = useState<Set<string>>(new Set());

  const isIpBasedIdentity = (session?.user as any)?.isIpBasedIdentity;

  const sanitizeHtml = (html: string) => {
    return html
      .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
      .replace(/style\s*=\s*["'][^"']*font-family[^"']*["']/gi, '');
  };

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const response = await listNotifications();
      console.log('response', response);
      if (response.success) {
        setNotificationsData(response.result);
      } else {
        console.error('Failed to load notifications');
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp: string) => {
    return dayjs(timestamp).format('DD-MM-YYYY HH:mm');
  };

  const allUnreadSelected =
    notificationsData.length > 0 && notificationsData.every((item) => selectedNotifications.includes(item.msgId));

  const toggleSelectAllUnread = () => {
    if (allUnreadSelected) {
      setSelectedNotifications([]);
    } else {
      setSelectedNotifications(notificationsData.map((item) => item.msgId));
    }
  };

  const handleCheckboxChange = (msgId: string, checked: boolean) => {
    if (checked) {
      setSelectedNotifications([...selectedNotifications, msgId]);
    } else {
      setSelectedNotifications(selectedNotifications.filter((id) => id !== msgId));
    }
  };

  const onPanelChange = async (item: Notification, isOpen: boolean) => {
    if (isOpen) {
      setExpandedPanels(new Set(expandedPanels).add(item.msgId));
      if (!item.read) {
        await saveRead(item);
      }
    } else {
      const newExpanded = new Set(expandedPanels);
      newExpanded.delete(item.msgId);
      setExpandedPanels(newExpanded);
    }
  };

  const saveRead = async (selectedMsg: Notification) => {
    try {
      await saveNotification({ ...selectedMsg, read: true });
      setNotificationsData(
        notificationsData.map((item) => (item.msgId === selectedMsg.msgId ? { ...item, read: true } : item)),
      );
    } catch (error) {
      console.error('Save failed:', error);
    }
  };

  const deleteNotification = async (msg: Notification) => {
    if (msg.read) {
      try {
        await saveNotification({ ...msg, isDeleted: true, read: true });
        setSelectedNotifications(selectedNotifications.filter((id) => id !== msg.msgId));
        setNotificationsData(notificationsData.filter((item) => item.msgId !== msg.msgId));
      } catch (error) {
        console.error('Delete failed:', error);
      }
    }
  };

  const markSelectedAsRead = async () => {
    for (const msgId of selectedNotifications) {
      const notification = notificationsData.find((n) => n.msgId === msgId);
      if (notification) {
        await saveRead(notification);
      }
    }
    setSelectedNotifications([]);
  };

  const deleteSelected = async () => {
    for (const msgId of selectedNotifications) {
      const notification = notificationsData.find((n) => n.msgId === msgId);
      if (notification) {
        await saveNotification({ ...notification, isDeleted: true, read: true });
      }
    }
    setNotificationsData(notificationsData.filter((item) => !selectedNotifications.includes(item.msgId)));
    setSelectedNotifications([]);
  };

  if (isIpBasedIdentity) {
    return (
      <div className="container mx-auto my-3">
        <div className="mb-4">
          <div className="mx-3 p-3">
            <h3 className="pt-6 pb-4 text-center text-xl font-bold">
              This feature is not available for IP-based users
            </h3>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto my-3 px-4 lg:px-2">
      <div className="mb-4">
        <div className="w-full">
          <AccountSectionTitle text="Stylus Alerts" description="Check here for your report alerts" />

          <div className="directories-wrapper">
            <div className="header-margin m-0 grid h-[70px] grid-cols-12 gap-2 p-0 md:gap-4">
              <div className="header-column-inp col-span-1 my-5 p-2">
                <Tooltip content={allUnreadSelected ? 'Deselect all' : 'Select all'} placement="top">
                  <input
                    type="checkbox"
                    checked={allUnreadSelected}
                    onChange={toggleSelectAllUnread}
                    disabled={notificationsData.length === 0}
                    className="h-4 w-4 cursor-pointer accent-black hover:bg-gray-200"
                  />
                </Tooltip>
              </div>
              <div className="header-m header-column-text col-span-2 my-5 p-2 text-[16px]">Date</div>
              <div className="header-m header-column-text col-span-7 my-5 p-2 text-[16px] md:col-span-8 lg:col-span-8">
                Subject
              </div>
              <div className="col-span-2 md:col-span-1 lg:col-span-1">
                <div className="action-btn-panel my-5 flex gap-2 md:gap-8">
                  <Tooltip content="Mark selected as read" placement="top" inactive={selectedNotifications.length < 1}>
                    <button
                      disabled={selectedNotifications.length < 1}
                      onClick={markSelectedAsRead}
                      className="cursor-pointer disabled:opacity-30"
                    >
                      <EyeLight className="h-5 w-5 md:h-7 md:w-7" />
                    </button>
                  </Tooltip>

                  <Tooltip content="Delete selected" placement="top" inactive={selectedNotifications.length < 1}>
                    <button
                      disabled={selectedNotifications.length < 1}
                      onClick={deleteSelected}
                      className="cursor-pointer disabled:opacity-30"
                    >
                      <TrashLight className="h-5 w-5 md:h-7 md:w-7" />
                    </button>
                  </Tooltip>
                </div>
              </div>
            </div>

            {!loading && notificationsData.length === 0 && (
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12">
                  <div className="mb-4 rounded border border-black p-4">No notifications</div>
                </div>
              </div>
            )}

            <div className="custom-expansion-panels border-none shadow-none">
              {notificationsData.map((item) => {
                const isExpanded = expandedPanels.has(item.msgId);
                return (
                  <div
                    key={item.msgId}
                    className={`panel-margin m-0 border-none shadow-none ${!item.read ? 'new-notification font-bold' : ''} ${selectedNotifications.includes(item.msgId) ? 'bg-gray-200' : ''}`}
                  >
                    <div
                      className="pnl-padding border-black-300 w-full cursor-pointer border-t p-0"
                      onClick={() => onPanelChange(item, !isExpanded)}
                    >
                      <div className="ma-2 grid grid-cols-12 gap-2 p-2 md:gap-4" style={{ width: '100%' }}>
                        <div
                          className="header-m col-span-1 m-0 flex items-center p-0"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            checked={selectedNotifications.includes(item.msgId)}
                            onChange={(e) => handleCheckboxChange(item.msgId, e.target.checked)}
                            className="h-4 w-4 cursor-pointer accent-black"
                          />
                        </div>
                        <div className="header-m col-span-2 m-0 flex items-center p-0 text-[15px]">
                          {formatDate(item.timestamp)}
                        </div>
                        <div className="header-m col-span-7 m-0 flex items-center p-0 text-[15px] md:col-span-8">
                          <div dangerouslySetInnerHTML={{ __html: item.subject }} />
                        </div>
                        <div className="header-m col-span-2 m-0 flex items-center gap-2 p-0 md:col-span-1 md:gap-2 lg:gap-8 xl:gap-10">
                          <Tooltip content="Delete" placement="right">
                            <button
                              disabled={!item.read}
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteNotification(item);
                              }}
                              className="cursor-pointer disabled:opacity-30"
                            >
                              <TrashLight className="h-5 w-5 md:h-6 md:w-6" />
                            </button>
                          </Tooltip>
                          <Tooltip content="Expand" placement="right">
                            <svg className="h-5 w-5 md:h-6 md:w-6" viewBox="0 0 24 24" fill="currentColor">
                              {isExpanded ? (
                                <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z" />
                              ) : (
                                <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
                              )}
                            </svg>
                          </Tooltip>
                        </div>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="p-4 text-sm">
                        <div
                          className="alert-content [&_a]:underline [&_div]:mb-2 [&_h3]:mb-4 [&_h3]:font-bold [&_p]:mb-4"
                          dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.message) }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
